import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as MuiIcons from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Collapse,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    MenuOutlined,
    ChevronLeft,
    Close,
    LogoutOutlined,
    SettingsOutlined,
    ExpandLess,
    ExpandMore,
} from "@mui/icons-material";
import { useDateRangeContext } from "contexts/DateRangeContext";
import { isTokenExpired } from "hooks/useAuthCheck";
import { useMenus } from "hooks/useMenus";
import { setLogout, setOpenSidebar } from "state";
import { NAV_HEIGHT } from "state/constants";
import api from 'state/axios';
import { updatePreferences } from "state/api";
import { disconnectSocket } from "state/socket";
import { logClientAction } from "state/logger";
import NavItems from "components/NavItems";
import UserImage from "components/UserImage";
import FlexColumn from "components/FlexColumn";


const openedWidth = 260;
const collapsedWidth = 68;

const Sidebar = ({ isSidebarOpen, isMobileOpen, onClose }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const token = useSelector((state) => state.token);
    const params = useSelector((state) => state.params);
    const user = useSelector((state) => state.user);
    const userId = user?._id;
    const isAdmin = user?.role === "admin";
    const permissions = user?.permissions || [];
    const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.toUpperCase();
    const picturePath = user?.picturePath || "/assets/User.png";
    
    const [preferences, setPreferences] = useState(user?.preferences || {});

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();

    const [isCollapsed, setIsCollapsed] = useState(!isSidebarOpen);
    
    // On mobile always show full sidebar (not collapsed)
    const effectiveCollapsed = isMobile ? false : isCollapsed;

    const [anchorEl, setAnchorEl] = useState(null);
    const [activeSub, setActiveSub] = useState(null);
    const [openSubmenus, setOpenSubmenus] = useState({});


    const { items: menuItems, error: menuError } = useMenus();

    const navItems = useMemo(() => {
        if (menuError || !Array.isArray(menuItems) || menuItems.length === 0) {
            return NavItems();
        }
        return menuItems;
    }, [menuItems, menuError]);

    const [collectionNavItems, setCollectionNavItems] = useState([]);

    useEffect(() => {
        const fetchCollections = async () => {
            try {
                const { data } = await api.get('/collectiondefs');
                const active = data.filter(c => c.isActive);
                if (!active.length) return;
                setCollectionNavItems([
                    { text: 'Modules', icon: null, path: null, group: 'modules', roles: [] },
                    ...active.map(c => {
                        const IconComp = c.icon ? MuiIcons[c.icon] : MuiIcons['TableChart'];
                        return {
                            text: c.label,
                            icon: IconComp ? <IconComp /> : null,
                            path: `modules/${c.name}`,
                            group: 'modules',
                            roles: [],
                        };
                    }),
                ]);
            } catch {
                // silently ignore — Modules section won't show if fetch fails
            }
        };
        fetchCollections();
    }, []);

    const allNavItems = useMemo(
        () => [...(navItems || []), ...collectionNavItems],
        [navItems, collectionNavItems]
    );

    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );

    const SIDEBAR_OPENED_WIDTH = Number(paramsMap.get("SIDEBAR_OPENED_WIDTH")) || openedWidth;
    const SIDEBAR_COLLAPSED_WIDTH = Number(paramsMap.get("SIDEBAR_COLLAPSED_WIDTH")) || collapsedWidth;


    // ================= Handlers =================
    const handleToggleSidebar = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        dispatch(setOpenSidebar({ isSidebarOpen: !newState }));

        const updated = { ...preferences, isSidebarOpen: !newState };
        setPreferences(updated);
        updatePreferences(userId, updated).catch(() => {});
    };

    const handleLogOut = useCallback(async () => {
        const url = '/auth/logout';
        await api.post(url, {}, { withCredentials: true });

        if (!isTokenExpired(token)) {
            logClientAction("logout", { email: user.email });
        }

        disconnectSocket(user._id);
        dispatch(setLogout());
    }, [token, user, dispatch]);

    const openSubMenu = (e, item) => {
        setAnchorEl(e.currentTarget);
        setActiveSub(item);
    };

    const closeSubMenu = () => {
        setAnchorEl(null);
        setActiveSub(null);
    };

    const toggleSubmenu = (key) => {
        setOpenSubmenus((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    // ================= Permission =================
    const hasRight = (permission, rightName) => {
        return permission?.right?.some(
            (r) => r.rightName === rightName && r.hasRight
        );
    }

    const hasGroupAccess = (items, permissions) => {
        return items.some(item => {
            if (item.path) {
                return permissions?.some(p => p.path?.trim() === item.path?.trim()
                    && hasRight(p, "read")
                );
            }

            if (item.subItems) {
                return hasGroupAccess(item.subItems, permissions);
            }

            return false;
        });
    };

    const hasAccessToItem = (item) => {
        if (!permissions.length) return false;

        if (isAdmin) {
            return (
                permissions.some((p) => p.path === item.path) ||
                item.subItems?.some(hasAccessToItem)
            );
        }

        if (item.path) {
            return permissions.some(
                (p) => p.path === item.path && hasRight(p, "read")
            );
        }

        if (item.subItems) {
            return item.subItems.some(hasAccessToItem);
        }

        return false;
    };

    const itemsByGroup = useMemo(() => {
        const map = {};

        allNavItems?.forEach(i => {
            if (!i.group) return;
            if (!map[i.group]) map[i.group] = [];
            map[i.group].push(i);
        });

        return map;
    }, [allNavItems]);

    const checkAccess = (item) => {
        if (item.isPublic) return true;

        if (!permissions?.length) return false;

        return (
            (item.group && !item.path &&
                hasGroupAccess(itemsByGroup[item.group] || [], permissions)) ||
            hasAccessToItem(item)
        );
    };

    // ================= Auto Open Active Submenu =================
    useEffect(() => {
        allNavItems?.forEach((item) => {
            if (
                item.subItems?.some((sub) => `/${sub.path}` === location.pathname)
            ) {
                setOpenSubmenus((prev) => ({
                    ...prev,
                    [item.text]: true,
                }));
            }
        });
    }, [location.pathname, allNavItems]);

    // ================= RENDER ITEM =================
    const renderItem = (item) => {
        const hasSub = item.subItems?.length;
        const isHeader = !item.path && !hasSub;

        // ===== Header =====
        if (isHeader) {
            if (!checkAccess(item)) return null;

            return (
                <Typography
                    key={item.text}
                    variant="h6"
                    color={theme.palette.primary.dark}
                    sx={{
                        px: effectiveCollapsed ? 0 : 2,
                        py: 1.5,
                        textAlign: effectiveCollapsed ? "center" : "left",
                    }}
                >
                    {item.text}
                </Typography>
            );
        }

        // ===== Permission =====
        if (!checkAccess(item)) return null;

        if (hasSub && !item.subItems.some(hasAccessToItem)) return null;

        const isActive = item.path ? location.pathname === `/${item.path}` : false;
        const isOpen = openSubmenus[item.text];

        const handleClick = (e) => {
            if (hasSub) {
                if (effectiveCollapsed) {
                    openSubMenu(e, item);
                } else {
                    toggleSubmenu(item.text);
                }
            } else if (item.action) {
                item.action();
            } else {
                navigate(`/${item.path}`);
                logClientAction(item.path, { startDate, endDate });
                if (isMobile && onClose) onClose();
            }
        };


        return (
            <React.Fragment key={item.text}>
                <ListItem disablePadding>
                    <Tooltip
                        title={effectiveCollapsed ? item.text : ""}
                        placement="right"
                        arrow
                    >
                        <ListItemButton
                            selected={isActive}
                            onClick={handleClick}
                            sx={{
                                justifyContent: effectiveCollapsed ? "center" : "flex-start",
                                py: 1.5,
                                borderLeft: "3px solid",
                                borderColor: isActive ? theme.palette.primary.main : "transparent",
                                color: isActive ? theme.palette.primary.main : "inherit",
                                fontWeight: isActive ? 700 : 400,
                                "&.Mui-selected": {
                                    color: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.18),
                                },
                            }}
                        >
                            {/* ICON */}
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: effectiveCollapsed ? 0 : 2,
                                    justifyContent: "center",                                    
                                    position: "relative",
                                    color: isActive ? theme.palette.primary.main : "inherit",
                                }}
                            >
                                {item.icon}

                                {/* DOT */}
                                {hasSub && effectiveCollapsed && (
                                    <Box
                                        sx={{
                                            width: 5,
                                            height: 5,
                                            bgcolor: theme.palette.neutral.darkest,
                                            borderRadius: "50%",
                                            position: "absolute",
                                            right: -12,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                        }}
                                    />
                                )}
                            </ListItemIcon>

                            {!effectiveCollapsed && <ListItemText primary={item.text} />}

                            {!effectiveCollapsed && hasSub && (
                                isOpen ? <ExpandLess /> : <ExpandMore />
                            )}
                        </ListItemButton>
                    </Tooltip>
                </ListItem>

                {/* EXPAND */}
                {!effectiveCollapsed && hasSub && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List sx={{ pl: 2 }}>
                            {item.subItems.map((sub) => {
                                const isSubActive = location.pathname === `/${sub.path}`;

                                return hasAccessToItem(sub) ? (
                                    <ListItemButton
                                        key={sub.text}
                                        selected={isSubActive}
                                        onClick={() => {
                                            navigate(`/${sub.path}`);
                                            logClientAction(sub.path, { startDate, endDate });
                                            if (isMobile && onClose) onClose();
                                        }}
                                        sx={{
                                            py: isMobile ? 1 : 1.5,
                                            borderLeft: "3px solid",
                                            borderColor: isSubActive ? theme.palette.primary.main : "transparent",
                                            color: isSubActive ? theme.palette.primary.main : "inherit",
                                            fontWeight: isSubActive ? 700 : 400,
                                            "&.Mui-selected": {
                                                color: theme.palette.primary.main,
                                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                            },
                                            "&.Mui-selected:hover": {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.18),
                                            },
                                        }}
                                    >
                                        <ListItemIcon 
                                            sx={{ minWidth: 0, 
                                                mr: 2, 
                                                color: isSubActive ? theme.palette.primary.main : "inherit" 
                                            }}
                                        >
                                            {sub.icon}
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={sub.text}                                             
                                        />
                                    </ListItemButton>
                                ) : null;
                            })}
                        </List>
                    </Collapse>
                )}

                {/* POPUP (collapsed desktop only) */}
                {effectiveCollapsed && hasSub && (
                    <Menu
                        anchorEl={anchorEl}
                        open={activeSub?.text === item.text}
                        onClose={closeSubMenu}
                        anchorOrigin={{
                            vertical: "center",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "center",
                            horizontal: "left",
                        }}
                        slotProps={{
                            paper: {
                                elevation: 8,
                                sx: {
                                    ml: 1,
                                    minWidth: 180,
                                    bgcolor: theme.palette.background.alt,
                                },
                            },
                        }}
                    >
                        {item.subItems.map((sub) => {
                            const isSubActive = location.pathname === `/${sub.path}`;
                                return hasAccessToItem(sub) ? (
                                    <MenuItem
                                        key={sub.text}
                                        onClick={() => {
                                            navigate(`/${sub.path}`);
                                            logClientAction(sub.path, { startDate, endDate });
                                            closeSubMenu();
                                        }}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            px: 2,
                                            py: 1.5,
                                            borderLeft: "3px solid",
                                            borderColor: isSubActive ? theme.palette.primary.main : "transparent",
                                            color: isSubActive ? theme.palette.primary.main : "inherit",
                                            fontWeight: isSubActive ? 700 : 400,
                                            bgcolor: isSubActive ? alpha(theme.palette.primary.main, 0.12) : "transparent",
                                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: isSubActive ? theme.palette.primary.main : "inherit"
                                            }}
                                        >
                                            {sub.icon}
                                        </Box>

                                        <Typography>
                                            {sub.text}
                                        </Typography>
                                    </MenuItem>
                                ) : null
                            }
                        )}
                    </Menu>
                )}
            </React.Fragment>
        );
    };


    const settingsItem = {
        text: "Settings",
        icon: <SettingsOutlined />,
        path: "settings",
        group: null,
        roles: [],
        isPublic: true,
    };

    const logoutItem = {
        text: "Log Out",
        icon: <LogoutOutlined />,
        path: "/",
        group: null,
        roles: [],
        isPublic: true,
        action: handleLogOut,
    };

    
    return (
        <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={isMobile ? isMobileOpen : true}
            onClose={onClose}
            ModalProps={{
                keepMounted: true,
            }}
            sx={{
                width: effectiveCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_OPENED_WIDTH,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                    width: effectiveCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_OPENED_WIDTH,
                    transition: "width 0.3s ease",
                    overflowX: "hidden",             
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "background.default",
                },
            }}
        >
            {/* ===== HEADER (STICKY) ===== */}
            <Box
                sx={{
                    height: NAV_HEIGHT,
                    position: "sticky",
                    top: 0,
                    zIndex: 99,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: effectiveCollapsed ? "center" : "space-between",
                    px: 2,
                    bgcolor: "background.alt",
                }}
            >
                {!effectiveCollapsed && (
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color={theme.palette.primary.dark}
                        sx={{
                            "&:hover": {
                                color: theme.palette.primary.main,
                                cursor: "pointer",
                            },
                        }}
                        onClick={() => navigate("/")}
                    >
                        DIGITAIR
                    </Typography>
                )}

                <Box display="flex" gap={1}>
                    {isMobile && (
                        <IconButton                                 
                            onClick={handleLogOut}
                        >
                            <LogoutOutlined />
                        </IconButton>
                    )}

                    <IconButton 
                        onClick={isMobile ? onClose : handleToggleSidebar}
                    >
                        {isMobile ? (
                            <Close />
                        ) : effectiveCollapsed ? (
                            <MenuOutlined />
                        ) : (
                            <ChevronLeft />
                        )}
                    </IconButton>
                </Box>
            </Box>

            {/* ===== SCROLLABLE CONTENT ===== */}
            <Box 
                sx={{ 
                    overflowY: "auto", 
                    flex: 1,
                }}
            >
                {!effectiveCollapsed && (
                    <FlexColumn mt={2} mb={1}>
                        <UserImage image={picturePath} size={60} />
                        <Typography
                            variant="h5"
                            color= {theme.palette.primary.dark}
                            sx={{ mt: 2 }}
                        >
                            {fullName}
                        </Typography>
                    </FlexColumn>
                )}

                <List>{allNavItems?.map((item) => renderItem(item))}</List>
                <List>
                    {renderItem(settingsItem)}
                    {renderItem(logoutItem)}
                </List>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
